import { Component, h, Method, Prop, State } from '@stencil/core';
import { EError } from '../../core/error';
import type { ICustomerExperienceComponent } from '../../models/customer-experience-component';
import type { RawServerResponse } from '../../models/raw-server-response';
import { getDefaultContext, type UXContext } from '../../models/ux-context';
import * as api from '../../services/api';

@Component({
    tag: 'dc-geo-modal',
    styleUrl: '../../assets/styles/style.scss',
    shadow: true,
})
export class DcGeoModal implements ICustomerExperienceComponent {
    @Prop() forceShow = false;
    @Prop() serverUrl?: string;
    @Prop() show = false;
    @State() customerExperienceContext: UXContext & RawServerResponse = getDefaultContext();
    @State() delayedShow = false;

    constructor() {
        this.fetchCustomerExperienceContext().catch(() => {
            throw new Error(EError.GetCustomerExperienceContext);
        });
    }

    /**
     * Get the customer experience object from the server, parse the response and update the modal view state
     */
    @Method()
    public async fetchCustomerExperienceContext(): Promise<UXContext & RawServerResponse> {
        this.customerExperienceContext = await api.fetchCustomerExperienceContext({
            serverUrl: this.serverUrl ? new URL(this.serverUrl) : api.getServerUrl(),
        });
        return this.customerExperienceContext;
    }

    /**
     * Getter for the customer experience object currently used by the modal component
     */
    @Method()
    public async getCustomerExperienceContext(): Promise<UXContext & RawServerResponse> {
        return this.customerExperienceContext;
    }

    /**
     * Inject the modal into the DOM
     */
    render() {
        if (!this.shouldShowModal()) {
            return;
        }

        setTimeout(() => {
            this.delayedShow = true;
        });

        return (
            <div
                class={`module-modal modal modal-dark modal-geo ${this.delayedShow && 'animate'} show`}
                data-plugin='moduleModal'
            >
                <div class='modal-dialog'>
                    <div class='modal-content'>
                        <div class='modal-header'>
                            <p class='headline h5 modal-title'>We apologize</p>
                        </div>
                        <div class='modal-body'>
                            <p>This service is not available in your location.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Check a customer experience object if the modal should be shown
     * @protected
     */
    protected shouldShowModal(): boolean {
        return (
            this.forceShow
            || (this.customerExperienceContext.systemActions.includes('showGeoBlockingModal')
                && (this.customerExperienceContext.isBlocked || this.show))
        );
    }
}
